import styled from "@emotion/styled/macro";
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { ReactNode, useState } from "react";
import { Row } from "../atoms/layoutAtoms";
import { FryProps, HamburgerMenu } from "./HamburgerMenu";

type CapCollapsibleTechnicalSpecificationsProps = {
  links: FryProps[] | null;
  menuLabel: string;
  isLoading?: boolean;
  isCustomer?: boolean;
  isNonSolar?: boolean;
  children: ReactNode;
};

const CapCollapsibleTechnicalSpecifications = ({
  links,
  menuLabel,
  isLoading = false,
  isCustomer = false,
  isNonSolar = false,
  children,
}: CapCollapsibleTechnicalSpecificationsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useSingletonMobileMediaQuery();

  return (
    <section data-testid="cap-technical-specifications">
      <HeaderRow isNonSolar={isNonSolar}>
        {isLoading ? (
          <HeaderContainerSkeleton />
        ) : (
          <>
            <Spacer />
            <HeaderContainer isMobile={isMobile}>
              Technical Specifications
            </HeaderContainer>
            <HeaderActions $isMobile={isMobile}>
              {links && !isCustomer ? (
                <HamburgerMenu
                  fries={links}
                  {...(!isMobile && { buttonLabel: menuLabel })}
                />
              ) : null}
              <CollapseChevronButton
                type="button"
                onClick={() => setIsExpanded((open) => !open)}
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? "Collapse technical specifications"
                    : "Expand technical specifications"
                }
              >
                <ChevronWrap $expanded={isExpanded} aria-hidden>
                  <SvgIcon
                    name={SvgNames.ChevronDown}
                    width={24}
                    height={24}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                </ChevronWrap>
              </CollapseChevronButton>
            </HeaderActions>
          </>
        )}
      </HeaderRow>
      {!isLoading && isExpanded ? (
        <Body data-testid="cap-technical-specifications-body">{children}</Body>
      ) : null}
    </section>
  );
};

const HeaderRow = styled(Row)<{ isNonSolar: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: ${({ isNonSolar }) => (isNonSolar ? "unset" : "100%")};
  background: #f4f1ec;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px rgba(208, 204, 195, 0.6) solid;
  padding: 0;
`;

const Spacer = styled.div`
  width: 56px;
`;

const HeaderContainer = styled.div<{ isMobile: boolean }>`
  color: #1f2647;
  line-height: ${({ isMobile }) => (isMobile ? "19px" : "22px")};
  font-size: ${({ isMobile }) => (isMobile ? "16px" : "19px")};
  font-family: "Roobert", sans-serif;
  font-weight: 500;
  word-wrap: break-word;
  padding: 24px;
`;

const HeaderContainerSkeleton = styled(Skeleton)`
  padding: 24px;
  flex: 1 1 0;
  text-align: center;
  color: #1f2647;
  font-size: 19px;
  font-family: "Roobert", sans-serif;
  font-weight: 500;
  word-wrap: break-word;
`;

const HeaderActions = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 24px;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    min-width: 32px;
  `
      : `
    overflow: visible;
  `}
`;

const CollapseChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

const ChevronWrap = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
`;

const Body = styled.div`
  width: 100%;
`;

export { CapCollapsibleTechnicalSpecifications };
